import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  labToCssRgb,
  type AlternativeMode,
  type ApiClientErrorCode,
  type Decor,
  type DecorKind,
} from '@pomoshnik/shared';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { Lightbox } from '@/components/Lightbox/Lightbox';
import { ForwardSheet } from '@/components/ForwardSheet/ForwardSheet';
import { buildCardShareUrl } from '@/lib/shareUrl';
import { AlternativesSection } from './AlternativesSection';

/**
 * Presenter Экрана 2.2.7 (Карточка декора). См. ADR-0005.
 *
 * 4 состояния (P-021): loading / success / not-found / error.
 *
 * Hero: color-block из dominantLab (если есть). Phase 7 MVP без реальных
 * R2-фото — заливаем фон цветом образца. Phase 9+ заменим signed URL.
 *
 * 4 vertical action buttons (PRD §5 Экран 2.2.7, design-system v0.2.2):
 *   1. «Показать на мебели» — Pinterest external link, new tab.
 *   2. «Альтернативы» — accordion expand → AlternativesSection (4 mood
 *      buttons). Disabled с reason для B-022 SKU без dominantLab (Phase 7 #2).
 *   3. «Найти в магазинах» — Link на `/decors/:kind/:globalId/shops`. Route
 *      будет реализован в Phase 8 (TODO в коде).
 *   4. «Переслать клиенту» — button → своё меню ForwardSheet (Фаза 6, P-019):
 *      WhatsApp/Telegram/MAX + «Скопировать сообщение» (clipboard в Container).
 *
 * Локальный UI-state `altsExpanded` (accordion) живёт в Presenter, не в URL.
 * Это допустимо ADR-0005 («useState разрешён ТОЛЬКО для локального UI-состояния»).
 */
export type DecorCardState = 'loading' | 'success' | 'not-found' | 'error';

export interface DecorCardViewProps {
  state: DecorCardState;
  decor?: Decor;
  error?: { code: ApiClientErrorCode; message: string };
  /** Фото-2 (PR-DATA4): signed-URL фото декора для hero. null/undefined или
   *  onError → color-block из dominantLab (как до Фото-2). */
  heroImageUrl?: string | null;
  onAlternativeMode: (mode: AlternativeMode) => void;
  /** Фаза 6 (item 4): «Скопировать сообщение» в меню пересылки — clipboard в Container. */
  onCopyMessage: (text: string) => void;
  onBackToCatalog: () => void;
}

const PINTEREST_BASE = 'https://www.pinterest.com/search/pins/';

/** Человеческое имя изделия per decor-kind (Фаза 6). */
const FORWARD_NOUN: Record<DecorKind, string> = {
  facade: 'Фасад',
  countertop: 'Столешница',
  panel: 'ЛДСП',
};
/** Pinterest-фраза per kind (Фаза 6, item 5): «мебель с фасадами/со столешницей/из ЛДСП». */
const PINTEREST_PHRASE: Record<DecorKind, string> = {
  facade: 'мебель с фасадами',
  countertop: 'мебель со столешницей',
  panel: 'мебель из ЛДСП',
};

function buildPinterestUrl(decor: Decor): string {
  // Фаза 6 (item 5): kind-фраза + название декора + бренд (раньше уходила
  // подкатегория/«мебель интерьер» без явной привязки к названию).
  const query = `${PINTEREST_PHRASE[decor.kind]} ${decor.name} ${decor.brand}`;
  return `${PINTEREST_BASE}?q=${encodeURIComponent(query)}`;
}

/**
 * Спец-ярлык для подкатегории «МДФ Пленка» фасадов (решение Aibek 2026-06-09):
 * «Пленка для фасадов» вместо «Фасад» — иначе на заглушке Greenwood
 * (name='Фасады пленка') выходит криво «Фасад, Фасады пленка». verbatim, заглавная
 * «П», НЕ склонять. Greenwood-заглушку (name='Фасады пленка') опускаем (дубль).
 */
const FACADE_FILM_SUBCATEGORY = 'МДФ Пленка'; // verbatim, без ё (P-001)
const FACADE_FILM_LABEL = 'Пленка для фасадов';
const FACADE_FILM_STUB_NAME = 'Фасады пленка'; // Greenwood name-заглушка

/** Сообщение пересылки БЕЗ URL (идёт в проп `message` ForwardSheet; ссылку
 *  компонент добавляет сам из `buildCardShareUrl`). */
function forwardTextNoUrl(decor: Decor): string {
  if (decor.kind === 'facade' && decor.subcategory === FACADE_FILM_SUBCATEGORY) {
    // Greenwood-заглушка → опускаем название (без «Пленка для фасадов, Фасады
    // пленка»); цветное название (AIF/Kira/EMC) — сохраняем после ярлыка.
    const namePart = decor.name === FACADE_FILM_STUB_NAME ? '' : `, ${decor.name}`;
    return `${FACADE_FILM_LABEL}${namePart}, ${decor.brand}, смотрите по ссылке`;
  }
  return `${FORWARD_NOUN[decor.kind]}, ${decor.name}, ${decor.brand}, смотрите по ссылке`;
}

export function DecorCardView(props: DecorCardViewProps) {
  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Декор" />
      <section className="flex-1 p-6 overflow-y-auto">
        {props.state === 'loading' && <LoadingState />}
        {props.state === 'success' && props.decor && (
          <SuccessState
            decor={props.decor}
            heroImageUrl={props.heroImageUrl}
            onAlternativeMode={props.onAlternativeMode}
            onCopyMessage={props.onCopyMessage}
          />
        )}
        {props.state === 'not-found' && <NotFoundState onBackToCatalog={props.onBackToCatalog} />}
        {props.state === 'error' && (
          <ErrorState
            message={props.error?.message ?? ''}
            onBackToCatalog={props.onBackToCatalog}
          />
        )}
      </section>
      <BottomBar />
    </main>
  );
}

function SuccessState({
  decor,
  heroImageUrl,
  onAlternativeMode,
  onCopyMessage,
}: {
  decor: Decor;
  heroImageUrl?: string | null;
  onAlternativeMode: (mode: AlternativeMode) => void;
  onCopyMessage: (text: string) => void;
}) {
  const [altsExpanded, setAltsExpanded] = useState(false);
  const [heroImgFailed, setHeroImgFailed] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // Зум фото — локальное состояние экрана (P-036): закрывается тапом/крестиком/
  // Esc внутри Lightbox, НЕ через bottom-bar «Назад» (та ведёт на пред. экран).
  const [zoomOpen, setZoomOpen] = useState(false);
  const heroBg = decor.dominantLab ? labToCssRgb(decor.dominantLab) : '#f0e8de';
  const altsEnabled = decor.dominantLab !== undefined;
  const showHeroImage = Boolean(heroImageUrl) && !heroImgFailed;

  // TODO(Phase 8): /decors/:kind/:globalId/shops route будет создан в Phase 8.
  // Сейчас при клике пользователь попадает на 404 — это OK для Phase 7,
  // route активируется в следующей фазе (ADR-0007 shops shared module).
  const shopsHref = `/decors/${encodeURIComponent(decor.kind)}/${encodeURIComponent(
    decor.globalId,
  )}/shops`;

  return (
    <div className="flex flex-col gap-5">
      {/* Hero: реальное фото из R2 (Фото-2), fallback — color-block из dominantLab.
          При наличии фото — тап открывает полноэкранный зум (Lightbox). */}
      {showHeroImage ? (
        <button
          type="button"
          onClick={() => setZoomOpen(true)}
          aria-label={`Открыть фото на весь экран: ${decor.name}`}
          className="block w-full aspect-video rounded-xl border-[1.5px] border-icon-border overflow-hidden bg-card p-0 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90"
        >
          <img
            src={heroImageUrl as string}
            alt=""
            className="w-full h-full object-cover"
            onError={() => setHeroImgFailed(true)}
          />
        </button>
      ) : (
        <div
          aria-label="Образец цвета декора"
          className="w-full aspect-video rounded-xl border-[1.5px] border-icon-border flex items-center justify-center font-display text-3xl text-ink overflow-hidden"
          style={{ background: heroBg }}
        >
          {decor.brand.charAt(0)}
        </div>
      )}
      {showHeroImage && zoomOpen && (
        <Lightbox
          src={heroImageUrl as string}
          alt={decor.name}
          onClose={() => setZoomOpen(false)}
        />
      )}

      {/* Name + brand. Внутренний Global_ID пользователю НЕ показываем (P-042). */}
      <div className="flex flex-col gap-2">
        <h2 className="font-display text-h2 text-ink m-0">{decor.name}</h2>
        <p className="text-muted text-base m-0">
          Бренд: <span className="text-ink font-semibold">{decor.brand}</span>
        </p>
      </div>

      {/* 4 vertical action buttons (P-019 MUST). */}
      <div className="flex flex-col gap-3">
        <a
          href={buildPinterestUrl(decor)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Показать на мебели: ${decor.name}`}
          className={[
            'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
            'text-[16px] py-3 px-4 flex items-center gap-3',
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
          ].join(' ')}
        >
          <span aria-hidden="true">▶</span>
          <span className="flex-1 text-left">Показать на мебели</span>
          <span aria-hidden="true" className="text-muted">
            ↗
          </span>
        </a>

        <button
          type="button"
          aria-expanded={altsExpanded}
          onClick={() => setAltsExpanded((v) => !v)}
          className={[
            'w-full min-h-tap rounded-lg bg-card border-[1.5px] text-ink',
            'text-[16px] py-3 px-4 flex items-center gap-3',
            altsExpanded ? 'border-cta' : 'border-accent',
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
          ].join(' ')}
        >
          <span aria-hidden="true">⚖</span>
          <span className="flex-1 text-left">Альтернативы</span>
          <span aria-hidden="true" className="text-muted">
            {altsExpanded ? '⌄' : '›'}
          </span>
        </button>
        {altsExpanded && (
          <div className="pl-3">
            <AlternativesSection enabled={altsEnabled} onSelect={onAlternativeMode} />
          </div>
        )}

        <Link
          to={shopsHref}
          aria-label={`Найти в магазинах: ${decor.name}`}
          className={[
            'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
            'text-[16px] py-3 px-4 flex items-center gap-3',
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
          ].join(' ')}
        >
          <span aria-hidden="true">🛒</span>
          <span className="flex-1 text-left">Найти в магазинах</span>
          <span aria-hidden="true" className="text-muted">
            ›
          </span>
        </Link>

        <button
          type="button"
          onClick={() => setShareOpen(true)}
          aria-label="Переслать клиенту"
          className={[
            'w-full min-h-tap rounded-lg bg-card border-[1.5px] border-accent text-ink',
            'text-[16px] py-3 px-4 flex items-center gap-3',
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
          ].join(' ')}
        >
          <span aria-hidden="true">↗</span>
          <span className="flex-1 text-left">Переслать клиенту</span>
        </button>
      </div>

      <ForwardSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        message={forwardTextNoUrl(decor)}
        url={buildCardShareUrl('decors', {
          kind: decor.kind,
          globalId: decor.globalId,
          shortId: decor.shortId,
        })}
        onCopyMessage={onCopyMessage}
      />
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3">
      <p className="text-muted text-[18px] m-0">Загружаем…</p>
    </div>
  );
}

function NotFoundState({ onBackToCatalog }: { onBackToCatalog: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center">
      <p className="text-muted text-[18px] max-w-[280px] m-0 leading-normal">Декор не найден</p>
      <button
        type="button"
        onClick={onBackToCatalog}
        className="min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink font-semibold text-base px-6 py-3"
      >
        Назад в каталог
      </button>
    </div>
  );
}

function ErrorState({
  message,
  onBackToCatalog,
}: {
  message: string;
  onBackToCatalog: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center">
      <p className="text-error text-[18px] max-w-[280px] m-0 leading-normal">
        Не удалось получить ответ. Проверьте интернет и попробуйте снова.
      </p>
      {message && <p className="text-muted text-sm">{message}</p>}
      <button
        type="button"
        onClick={onBackToCatalog}
        className="min-h-tap rounded-lg bg-cta border-b-2 border-cta-shadow text-ink font-semibold text-base px-6 py-3"
      >
        Назад в каталог
      </button>
    </div>
  );
}
